import api from "../config/api";
import { getErrorMessage } from "../config/errorHelper";

// ─────────────────────────────────────────────
//  ZONE CLIENT
// ─────────────────────────────────────────────

/**
 * Liste les tickets de l'entreprise de l'utilisateur connecté.
 * @param {string} status  filtre optionnel (OPEN, IN_PROGRESS, RESOLVED…)
 */
export const getMyTickets = async (status = null) => {
  try {
    const query = status ? `?status=${status}` : "";
    const res = await api.get(`/tickets${query}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/** Détail d'un ticket. */
export const getTicket = async (id) => {
  try {
    const res = await api.get(`/tickets/${id}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/** Créer un ticket. payload : { title, description, category, priority } */
export const createTicket = async (payload) => {
  try {
    const res = await api.post("/tickets", payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** Modifier le statut / priorité d'un ticket. */
export const updateTicket = async (id, payload) => {
  try {
    const res = await api.put(`/tickets/${id}`, payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** Commentaires d'un ticket. */
export const getComments = async (ticketId) => {
  try {
    const res = await api.get(`/tickets/${ticketId}/comments`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/** Ajouter un commentaire. payload : { content } */
export const addComment = async (ticketId, payload) => {
  try {
    const res = await api.post(`/tickets/${ticketId}/comments`, payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

// ─────────────────────────────────────────────
//  ZONE ADMIN — /api/tickets/admin/*
//  Le SUPER_ADMIN gère tous les tickets de toutes les entreprises.
// ─────────────────────────────────────────────

/**
 * Liste tous les tickets — toutes entreprises confondues.
 *
 * @param {{ companyId?, status?, priority? }} filters
 */
export const adminGetTickets = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.companyId) params.append("companyId", filters.companyId);
    if (filters.status)    params.append("status",    filters.status);
    if (filters.priority)  params.append("priority",  filters.priority);
    const query = params.toString() ? `?${params}` : "";
    const res = await api.get(`/tickets/admin/all${query}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/** Détail d'un ticket — admin. */
export const adminGetTicket = async (id) => {
  try {
    const res = await api.get(`/tickets/admin/${id}`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Créer un ticket pour une entreprise.
 * payload : { title, description, category, priority, companyId }
 */
export const adminCreateTicket = async (payload) => {
  try {
    const res = await api.post("/tickets/admin/create", payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/**
 * Modifier un ticket — admin sans restriction.
 * payload : { title?, description?, status?, priority?, category? }
 */
export const adminUpdateTicket = async (id, payload) => {
  try {
    const res = await api.put(`/tickets/admin/${id}`, payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** Supprimer un ticket. */
export const adminDeleteTicket = async (id) => {
  try {
    const res = await api.delete(`/tickets/admin/${id}`);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};

/** Commentaires d'un ticket — admin. */
export const adminGetComments = async (ticketId) => {
  try {
    const res = await api.get(`/tickets/admin/${ticketId}/comments`);
    return res.data;
  } catch (err) {
    throw new Error(getErrorMessage(err));
  }
};

/** Ajouter un commentaire — admin. */
export const adminAddComment = async (ticketId, payload) => {
  try {
    const res = await api.post(`/tickets/admin/${ticketId}/comments`, payload);
    if (res.data?.code === "ERROR") throw new Error(res.data.message);
    return res.data;
  } catch (err) {
    if (!(err.response || err.request)) throw err;
    throw new Error(getErrorMessage(err));
  }
};